import { CSSResult, css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators';

import { Select } from '@material/mwc-select';
import { isDefined } from '../../helpers/utils';

@customElement('proxi-multi-selectfield')
export class ProxiMultiSelectfield extends Select {
    @property({ type: String }) height: string = '2rem';
    @property({ type: Array }) options:Array<{text: string; value: any}> = [];
    @property({ type: Array }) selectedValues: Array<{text: string; value: any}> = [];
    @property({ type: String }) svg: string = `<svg class="mdc-select__dropdown-icon-graphic" viewBox="7 10 10 5" focusable="false">
              <polygon class="mdc-select__dropdown-icon-inactive" stroke="none" fill-rule="evenodd" points="7 10 12 15 17 10">
              </polygon>
              <polygon class="mdc-select__dropdown-icon-active" stroke="none" fill-rule="evenodd" points="7 15 12 10 17 15">
              </polygon>
            </svg>`;

    @state() private showDropdown: boolean = false;

    static get styles(): CSSResult[] {
        return [
            ...Select.styles,
            css`
                :host {
                    width: 100%;
                    --mdc-menu-item-height: var(--select-height, 2.5rem);
                    --mdc-typography-subtitle1-font-size: 12px;
                }

                .mdc-text-field--with-leading-icon.mdc-text-field--outlined .mdc-floating-label--float-above {
                    transform: translateY(-30.25px) translateX(-32px) scale(1);
                }

                .mdc-select--outlined .mdc-select--filled .mdc-select__anchor {
                    height: var(--select-height, 2.5rem) !important;
                    width: 100%;
                    border-radius: 4px;
                }

                .custom-select-anchor {
                    height: var(--select-height, 2.5rem) !important;
                    width: 100% !important;
                    display: flex;
                    align-items: center;
                    border: 1px solid var(--main-primary-color, #ccc);
                    border-radius: 4px;
                    padding: 0 8px;
                    overflow: hidden;
                }

                .pill {
                    background-color: #e0e0e0;
                    border-radius: 4px;
                    padding: 0 8px;
                    margin: 2px;
                    display: flex;
                    align-items: center;
                }

                .pill span {
                    margin-left: 4px;
                    margin-right: 4px;
                    white-space: nowrap;
                }

                .pill button {
                    background: none;
                    border: none;
                    cursor: pointer;
                    padding: 0;
                    margin: 0;
                }

                .dropdown {
                    background: white;
                    border: 1px solid #ccc;
                    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
                    z-index: 1000;
                    width: auto;
                    max-width: 300px;
                    max-height: 200px;
                    overflow-y: auto;
                }

                .dropdown-item {
                    display: flex;
                    align-items: center;
                    padding: 4px 8px;
                }

                .dropdown-item input {
                    margin-right: 8px;
                }

                input[type="checkbox"] {
                    color: var(--main-primary-color, #007bff);
                }
            `
        ];
    }

    async firstUpdated() {
        const selectAnchor = this.shadowRoot?.querySelector('.mdc-select__anchor') as HTMLElement;
        if (isDefined(selectAnchor)) {
            selectAnchor.classList.add('custom-select-anchor');
        }
        document.addEventListener('click', this.handleClickOutside.bind(this));
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        document.removeEventListener('click', this.handleClickOutside.bind(this));
    }

    private handleClickOutside(event: MouseEvent) {
        const path = event.composedPath();
        if (!path.includes(this)) {
            this.showDropdown = false;
        }
    }

    updated(changedProperties: Map<string | number | symbol, unknown>) {
        if (changedProperties.has('height')) {
            this.style.setProperty('--select-height', this.height);
        }
    }

    private toggleDropdown() {
        this.showDropdown = !this.showDropdown;
    }

    private handleCheckboxChange(selectedValue: string, event: CustomEvent) {
        const text = selectedValue;
        const value = this.options.find(option => option.text === text)?.value;
        const value_object: {text: string; value: any} = {text, value};
        const checked =(event.detail as HTMLInputElement).checked;

        if (checked) {
            this.selectedValues = [...this.selectedValues, value_object];
        } else {
            this.selectedValues = this.selectedValues.filter(v => v !== value_object);
        }

        this.dispatchEvent(new CustomEvent('selection-changed', { detail: { selectedValues: this.selectedValues } }));
    }

    private removePill(value: string) {
        this.selectedValues = this.selectedValues.filter(v => v.text !== value);
        this.dispatchEvent(new CustomEvent('selection-changed', { detail: { selectedValues: this.selectedValues } }));
    }



    render() {
        return html`
            <div class="mdc-select__anchor" @click="${this.toggleDropdown}">
                ${this.selectedValues.length > 1 ? html`
                    <div class="pill" style="background-color:var(--main-primary-color, #007bff); padding: 0 2px; color: white;">
                        <span>${this.selectedValues.length}</span>
                    </div>
                ` : ''}
                ${this.selectedValues.map(value => html`
                    <div class="pill">
                        <span>${value.text}</span>
                        <button @click="${() => this.removePill(value.text)}">&times;</button>
                    </div>
                `)}
                ${this.selectedValues.length === 0 ? html`<span class="mdc-select__selected-text">
                    ${this.title || ''}
                </span>` : ''}
                ${this.selectedValues.length > 1 ? html`<mwc-icon style="--mdc-icon-size: .9rem;">keyboard_arrow_down</mwc-icon>` : ''}
            </div>
            ${this.showDropdown ? html`
                <div class="dropdown">
                    ${this.options.map(option => html`
                        <div class="dropdown-item">
                            <form-checkbox
                                .value="${option}"
                                ?checked="${this.selectedValues.some(v => v.text === option.text)}"
                                @change="${(e: any) => this.handleCheckboxChange(option.text, e)}">
                                    <span>${option.text}</span>
                            </form-checkbox>
                        </div>
                    `)}
                </div>
            ` : ''}
        `;
    }
}